import { FC, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import { Shield } from 'lucide-react'
import { cn } from '../lib/utils'

interface PrivacyPolicyDialogProps {
  className?: string
  triggerClassName?: string
  linkText?: string
  variant?: 'link' | 'button'
}

const PrivacyPolicyDialog: FC<PrivacyPolicyDialogProps> = ({
  className,
  triggerClassName,
  linkText = 'Privacy Policy',
  variant = 'link',
}) => {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div
          className={cn(
            'inline-flex items-center gap-1.5 cursor-pointer transition-colors',
            variant === 'link' &&
            'text-sumoPrimary hover:text-sumoOrange underline underline-offset-4',
            variant === 'button' &&
            'text-neutral700 dark:text-neutral300 hover:text-sumoPrimary dark:hover:text-sumoPrimary',
            triggerClassName
          )}
        >
          <Shield className="w-4 h-4" />
          <span className="text-sm font-medium">{linkText}</span>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-sumoPrimary" />
            Privacy Policy
          </DialogTitle>
          <DialogDescription>
            Learn how we collect, use, and protect your information
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-6 text-sm text-neutral700 dark:text-neutral300">
          {/* Last Updated */}
          <div className="text-xs text-neutral500 dark:text-neutral500">
            <strong>Last Updated:</strong> January 2025
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              1. Introduction
            </h2>
            <div className="p-4 bg-sumoPrimary/10 border border-sumoPrimary rounded-lg mb-3">
              <p className="font-semibold text-neutral900 dark:text-neutral100 mb-2">
                BY USING THE SERVICE, YOU CONSENT TO THE DATA PRACTICES DESCRIBED IN THIS PRIVACY
                POLICY.
              </p>
            </div>
            <p>
              InventorySumo ("we", "us", "our") is committed to protecting your privacy. This
              Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you use our inventory management application.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              2. Information We Collect
            </h2>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              2.1 Information You Provide
            </h3>
            <div className="space-y-2 mb-3">
              <p className="font-medium">Account Information:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Name and email address</li>
                <li>Password (encrypted)</li>
                <li>Account timestamps</li>
              </ul>

              <p className="font-medium mt-3">Subscription Information:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Subscription tier and status</li>
                <li>Payment information (via Lemon Squeezy)</li>
                <li>Billing history</li>
              </ul>

              <p className="font-medium mt-3">User Data (Inventory):</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Inventory items, SKUs, quantities, and values</li>
                <li>Storage units, bins, and categories</li>
                <li>Product listings and sales transactions</li>
                <li>Barcodes and custom metadata</li>
              </ul>
            </div>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              2.2 Automatically Collected Information
            </h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Usage data (pages visited, features used, time stamps)</li>
              <li>Device information (browser type, OS, IP address)</li>
              <li>Cookies and tracking technologies</li>
            </ul>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              2.3 Third-Party Information
            </h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Payment processor confirmations (Lemon Squeezy)</li>
              <li>We do NOT store credit card information</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Provide the Service:</strong> Create accounts, process subscriptions, store
                inventory data
              </li>
              <li>
                <strong>Improve the Service:</strong> Analyze usage patterns, develop new features
              </li>
              <li>
                <strong>Communicate:</strong> Support requests, technical notices, security alerts
              </li>
              <li>
                <strong>Ensure Security:</strong> Monitor suspicious activity, prevent fraud
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              4. How We Share Your Information
            </h2>
            <div className="p-4 bg-success500/10 border border-success500 rounded-lg mb-3">
              <p className="font-semibold text-success700 dark:text-success300">
                We do NOT sell your personal information to third parties.
              </p>
            </div>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              We Share Information Only With:
            </h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Firebase/Google Cloud Platform:</strong> Hosts database and authentication
              </li>
              <li>
                <strong>Lemon Squeezy:</strong> Processes payments and manages billing
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to protect rights
              </li>
              <li>
                <strong>Business Transfers:</strong> In case of merger or acquisition (with notice)
              </li>
              <li>
                <strong>With Your Consent:</strong> For any other purpose you explicitly approve
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              5. Data Storage and Security
            </h2>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              5.1 Storage Location
            </h3>
            <p className="mb-3">
              Your data is stored on Google Cloud Platform servers in the United States. By using
              the Service, you consent to this data transfer.
            </p>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              5.2 Security Measures
            </h3>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
              <li>Encryption of data in transit (TLS/SSL)</li>
              <li>Encryption of data at rest</li>
              <li>Firebase Authentication security</li>
              <li>Regular security audits</li>
              <li>Secure password hashing</li>
            </ul>

            <div className="p-4 bg-warning500/10 border-2 border-warning500 rounded-lg">
              <p className="font-semibold text-warning700 dark:text-warning300 mb-2">
                IMPORTANT: No Security is Absolute
              </p>
              <p className="text-sm">
                While we implement industry-standard security measures, we cannot guarantee 100%
                security. You are responsible for maintaining the confidentiality of your password
                and creating backup copies of your data.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              6. Your Privacy Rights
            </h2>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              You Have the Right To:
            </h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Access:</strong> Request a copy of your personal information
              </li>
              <li>
                <strong>Correction:</strong> Update your account information anytime
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your account and data
              </li>
              <li>
                <strong>Portability:</strong> Request your data in machine-readable format
              </li>
              <li>
                <strong>Opt-Out:</strong> Unsubscribe from marketing emails
              </li>
              <li>
                <strong>Cookie Control:</strong> Manage cookies through browser settings
              </li>
            </ul>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              California Residents (CCPA)
            </h3>
            <p className="mb-2">Additional rights under California Consumer Privacy Act:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Right to know what information is collected</li>
              <li>Right to opt-out of sale (we do NOT sell your data)</li>
              <li>Right to non-discrimination</li>
            </ul>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              European Residents (GDPR)
            </h3>
            <p className="mb-2">Rights under General Data Protection Regulation:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Right to access, correction, and deletion</li>
              <li>Right to restrict or object to processing</li>
              <li>Right to data portability</li>
              <li>Right to withdraw consent</li>
              <li>Right to lodge a complaint with supervisory authority</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              7. Children's Privacy
            </h2>
            <div className="p-4 bg-error500/10 border border-error500 rounded-lg">
              <p className="font-semibold text-error700 dark:text-error300">
                The Service is NOT intended for children under 13 (or 16 in the EEA).
              </p>
              <p className="text-sm mt-2">
                We do not knowingly collect information from children. If you believe we have
                collected information from a child, contact us immediately for deletion.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              8. Third-Party Services
            </h2>
            <p className="mb-2">
              The Service may integrate with third-party services (eBay, Facebook Marketplace,
              etc.). We are NOT responsible for their privacy practices.
            </p>
            <p className="text-sm text-neutral600 dark:text-neutral400">
              Third-party services have their own privacy policies. Please review them before using
              these integrations.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              9. Data Retention
            </h2>
            <p className="mb-2">
              We retain your information while your account is active or as needed to provide the
              Service.
            </p>
            <p className="font-medium mb-2">After Account Deletion:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>User Data deleted within 30 days</li>
              <li>Backup systems may retain data up to 90 days</li>
              <li>Some information retained as required by law</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              10. Changes to This Policy
            </h2>
            <p className="mb-2">We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Posting the updated policy on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending email notifications (for significant changes)</li>
              <li>Displaying in-app notifications</li>
            </ul>
            <p className="mt-3 font-medium">
              Your continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              11. Contact Us
            </h2>
            <p className="mb-2">
              For privacy-related questions, concerns, or requests (data access, deletion, etc.),
              contact us at:
            </p>
            <div className="p-4 bg-neutral50 dark:bg-neutral900 border border-neutral200 dark:border-neutral700 rounded-lg">
              <p className="font-semibold">InventorySumo Privacy Team</p>
              <p className="text-sm mt-1">nate@ntdevelopmentllc.com</p>
              <p className="text-xs text-neutral500 dark:text-neutral400 mt-2">
                Response time: Typically within 10 days
              </p>
            </div>
          </section>

          {/* Summary */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              Quick Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-success500">✓</span>
                <span>We collect account, subscription, and inventory data</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-success500">✓</span>
                <span>We use Firebase/Google Cloud for hosting</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-success500">✓</span>
                <span>We do NOT sell your information</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-success500">✓</span>
                <span>You can request data access/deletion anytime</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-success500">✓</span>
                <span>You're responsible for data backups</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-success500">✓</span>
                <span>Data stored in the United States</span>
              </div>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="border-t-2 border-neutral200 dark:border-neutral700 pt-6">
            <div className="p-4 bg-sumoPrimary/10 border-2 border-sumoPrimary rounded-lg">
              <p className="font-semibold text-neutral900 dark:text-neutral100">
                BY USING INVENTORYSUMO, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS
                PRIVACY POLICY AND CONSENT TO OUR DATA PRACTICES.
              </p>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PrivacyPolicyDialog
